import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Receipt, Users, Wallet, BarChart3, PieChart, RefreshCcw, FileDigit, Check } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const icons = {
  muhasebe: Calculator,
  vergi: Receipt,
  sgk: Users,
  bordro: Wallet,
  rapor: BarChart3,
  butce: PieChart,
  kdv: RefreshCcw,
  edefter: FileDigit,
};

const bullets = {
  tr: {
    muhasebe: ["Yasal defter tutma", "Aylık-yıllık beyannameler", "BA-BS uyumu", "Bilanço & gelir tablosu"],
    vergi: ["Kurumlar & gelir vergisi", "Stopaj planlaması", "KDV stratejisi", "Vergi incelemesi temsili"],
    sgk: ["İşe giriş-çıkış işlemleri", "SGK teşvik takibi", "Denetim temsiliyeti", "Yabancı personel izinleri"],
    bordro: ["Aylık bordro", "Kıdem-ihbar hesabı", "Tazminat planlaması", "AGİ ve kesinti raporları"],
    rapor: ["IFRS/TFRS raporlama", "Yönetim raporları", "KPI dashboardları", "Yatırımcı sunumları"],
    butce: ["Yıllık bütçeleme", "Nakit akış projeksiyonu", "Sapma analizleri", "Maliyet kontrol sistemi"],
    kdv: ["İhracat KDV iadesi", "İndirimli oran iadesi", "Yatırım teşvikli iade", "GİB süreç yönetimi"],
    edefter: ["e-Defter beratlama", "e-Fatura & e-Arşiv", "e-İrsaliye, e-SMM", "GİB entegrasyonu"],
  },
  en: {
    muhasebe: ["Statutory bookkeeping", "Monthly-annual filings", "BA-BS reconciliation", "Balance sheet & P&L"],
    vergi: ["Corporate & income tax", "Withholding planning", "VAT strategy", "Tax-audit representation"],
    sgk: ["Employee declarations", "Incentive tracking", "Audit representation", "Expat work permits"],
    bordro: ["Monthly payroll", "Severance & notice", "Indemnity planning", "AGI & deduction reports"],
    rapor: ["IFRS/TFRS reporting", "Management reports", "KPI dashboards", "Investor decks"],
    butce: ["Annual budgeting", "Cash-flow projections", "Variance analysis", "Cost-control systems"],
    kdv: ["Export VAT refund", "Reduced-rate refund", "Investment-incentive refund", "GİB process management"],
    edefter: ["e-Ledger sealing", "e-Invoice & e-Archive", "e-Waybill, e-SMM", "GİB integration"],
  },
};

const Services = () => {
  const { t, lang } = useLanguage();

  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="pt-20 pb-12 md:pt-28 md:pb-16">
        <div className="container-rotam">
          <p className="overline">{t.services.overline}</p>
          <h1 className="mt-4 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            {t.services.title}
          </h1>
          <p className="mt-6 text-rotam-muted text-base md:text-lg max-w-2xl leading-relaxed">
            {t.services.description}
          </p>
        </div>
      </section>

      {/* Service detail blocks */}
      <section className="pb-24 md:pb-32">
        <div className="container-rotam space-y-px bg-rotam-border border border-rotam-border">
          {t.services.items.map((s, idx) => {
            const Icon = icons[s.key];
            const list = bullets[lang][s.key] || [];
            return (
              <div
                key={s.key}
                data-testid={`service-detail-${s.key}`}
                className="bg-rotam-bg p-8 md:p-12 grid lg:grid-cols-12 gap-8 items-start"
              >
                <div className="lg:col-span-1 flex lg:flex-col items-center lg:items-start gap-4">
                  <span className="text-xs tracking-[0.25em] text-rotam-accent font-semibold">
                    0{idx + 1}
                  </span>
                  <div className="w-12 h-12 bg-rotam-primary/10 text-rotam-primary flex items-center justify-center rounded-sm">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-rotam-ink">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-rotam-muted leading-relaxed max-w-xl">{s.desc}</p>
                </div>
                <div className="lg:col-span-5">
                  <ul className="space-y-3">
                    {list.map((b, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-rotam-ink">
                        <Check size={16} className="text-rotam-accent shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <div className="container-rotam mt-16 flex justify-center">
          <Link to="/contact" data-testid="services-cta" className="btn-primary">
            {t.home.ctaButton} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
